export type DynamoDBStringAttribute = {
  S: string
  [key: string]: unknown
}

/**
 * DynamoDBの属性値が文字列型かどうかを判定します。
 */
export const isDynamoDBStringAttribute = (value: unknown): value is DynamoDBStringAttribute =>
  typeof value === "object" &&
  value !== null &&
  "S" in (value as Record<string, unknown>) &&
  typeof (value as { S?: unknown }).S === "string"

/**
 * DynamoDB属性値や通常の文字列から文字列を抽出します。
 */
export const getAttributeString = (value: unknown, fallback = ""): string => {
  if (typeof value === "string") {
    return value
  }

  if (isDynamoDBStringAttribute(value)) {
    return value.S
  }

  return fallback
}

/**
 * 文字列化されたJSON、またはそのままオブジェクトとして渡された値をオブジェクトに変換します。
 */
export const getAttributeRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value) {
    return null
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    if (isDynamoDBStringAttribute(value)) {
      return parseJsonSafely(value.S)
    }
    return value as Record<string, unknown>
  }

  if (typeof value === "string") {
    return parseJsonSafely(value)
  }

  return null
}

/**
 * DynamoDBのタイムスタンプ文字列を日付フォーマットに変換します
 */
export const formatDateTime = (timestamp: unknown): string => {
  const timestampStr = getAttributeString(timestamp);
  if (!timestampStr) return '不明';
  
  try {
    // YYYYMMDDHHMMSS形式の場合
    if (timestampStr.length === 14) {
      const year = timestampStr.substring(0, 4);
      const month = timestampStr.substring(4, 6);
      const day = timestampStr.substring(6, 8);
      const hour = timestampStr.substring(8, 10);
      const minute = timestampStr.substring(10, 12);
      const second = timestampStr.substring(12, 14);
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    }
    return timestampStr;
  } catch {
    return timestampStr;
  }
};

/**
 * DynamoDBのタイムスタンプ文字列を日付のみのフォーマットに変換します
 */
export const formatDate = (timestamp: unknown): string => {
  const timestampStr = getAttributeString(timestamp);
  if (!timestampStr) return '不明';
  
  try {
    // YYYYMMDDHHMMSS形式の場合
    if (timestampStr.length === 14) {
      const year = timestampStr.substring(0, 4);
      const month = timestampStr.substring(4, 6);
      const day = timestampStr.substring(6, 8);
      return `${year}-${month}-${day}`;
    }
    return timestampStr;
  } catch {
    return timestampStr;
  }
};

const parseJsonSafely = (payload: string): Record<string, unknown> | null => {
  try {
    return JSON.parse(payload) as Record<string, unknown>
  } catch {
    return null
  }
}
